import { computed, observable, runInAction } from 'mobx'
import type { ListBundlesFn } from '@/features/admin'
import type { Bundle } from '@/features/admin/bundle/Bundle'
import type {
  GetCurrentUserFn,
  GetWorkspaceRolesFn,
  GetWorkspaceSkillsFn,
  ListRolesFn,
  ListSkillsFn,
} from '@/features/iam'
import type { Membership } from '@/features/iam/workspaces/Membership'
import type { ListInventoryItemsFn } from '@/features/inventory'
import type { InventoryItem } from '@/features/inventory/InventoryItem'
import type { LegendServiceType } from '@/features/jobs/legends/legend-service-type'
import type { AlertRetryWrapperFactory } from '@/features/shell'
import { Id } from '@/infra/id'
import type {
  NamedItem,
  WorkspaceCatalog,
} from '@/features/legends/editor/legend-part-dependencies'

/**
 * Dependencies of the workspace catalog.
 */
export interface LegendWorkspaceCatalogOptions {
  /**
   * The user's membership in the legend's workspace, once the legend loads.
   */
  getMembership: () => Membership | null
  getServiceType: () => LegendServiceType | null
  getCurrentUser: GetCurrentUserFn
  listSkills: ListSkillsFn
  getWorkspaceSkills: GetWorkspaceSkillsFn
  listRoles: ListRolesFn
  getWorkspaceRoles: GetWorkspaceRolesFn
  listInventoryItems: ListInventoryItemsFn
  listBundles: ListBundlesFn
  createAlertRetryWrapper: AlertRetryWrapperFactory
}

/**
 * What the legend's workspace offers the builders to pick from: skills,
 * roles, service items and bundles.
 */
export class LegendWorkspaceCatalog implements WorkspaceCatalog {
  @observable.ref accessor inventoryItems: InventoryItem[] = []
  @observable.ref accessor bundles: Bundle[] = []

  constructor(private readonly _options: LegendWorkspaceCatalogOptions) {}

  @computed
  get skills(): NamedItem[] {
    const workspaceId = this._workspaceId
    return workspaceId
      ? this._options
          .getWorkspaceSkills(workspaceId)
          .flatMap((s) => (Id.some(s.id) ? [{ id: s.id, name: s.name }] : []))
      : []
  }

  @computed
  get roles(): NamedItem[] {
    const workspaceId = this._workspaceId
    return workspaceId
      ? this._options
          .getWorkspaceRoles(workspaceId)
          .flatMap((r) => (Id.some(r.id) ? [{ id: r.id, name: r.name }] : []))
      : []
  }

  /** @inheritDoc */
  @computed
  get allowedInventoryItems() {
    const serviceType = this._options.getServiceType()
    return this.inventoryItems.filter((item) => {
      const type = item.typeSpecification.type
      switch (serviceType) {
        case 'Bookkeeping':
          return type === 'Bookkeeping' || type === 'Default'
        case 'Consultation':
          return type === 'Consultation' || type === 'Default'
        case 'Tax':
          return type !== 'Bookkeeping'
        case 'Misc':
          return type === 'Default' || type === 'Extension'
        case null:
          return true
      }
    })
  }

  private get _workspaceId() {
    return this._options.getMembership()?.workspace.id
  }

  /**
   * Loads everything, from the legend's workspace.
   *
   * @param signal
   */
  fetch(signal?: AbortSignal) {
    const workspaceId = this._workspaceId
    if (!Id.some(workspaceId)) {
      return
    }

    return Promise.all([
      this._options.listSkills({ workspaceId, limit: 9999 }, signal),
      this._options.listRoles({ workspaceId }, signal),
      this.fetchInventoryItems(signal),
      this._fetchBundles(signal),
    ])
  }

  /**
   * Fetches the workspace's service items, when the user may see them.
   *
   * @param signal
   */
  async fetchInventoryItems(signal?: AbortSignal) {
    const workspaceId = this._workspaceId
    const permissions = this._options.getMembership()?.member.permissions
    const isAllowed =
      permissions?.adminManageGlobalInventoryItems ||
      permissions?.manageWorkspaceInventoryItems ||
      this._options.getCurrentUser().platformAdmin
    if (!Id.some(workspaceId) || !isAllowed) {
      return
    }

    const { data } = await this._options.createAlertRetryWrapper(
      'Loading the service items failed',
      signal,
    )(() =>
      this._options.listInventoryItems(
        {
          workspaceIds: [workspaceId],
          archivedStatus: 'Active',
          limit: 1000,
        },
        signal,
      ),
    )
    runInAction(() => {
      this.inventoryItems = data
    })
  }

  private async _fetchBundles(signal?: AbortSignal) {
    const workspaceId = this._workspaceId
    const permissions = this._options.getMembership()?.member.permissions
    const isAllowed =
      permissions?.manageWorkspaceInventoryItems ||
      this._options.getCurrentUser().platformAdmin
    if (!Id.some(workspaceId) || !isAllowed) {
      return
    }

    const { data } = await this._options.createAlertRetryWrapper(
      'Loading the bundles failed',
      signal,
    )(() =>
      this._options.listBundles(
        {
          workspaceId,
          archivedStatus: 'Active',
          orderBy: 'name',
          limit: 1000,
          after: null,
        },
        signal,
      ),
    )
    runInAction(() => {
      this.bundles = data
    })
  }
}
